import { getImageUrl } from '@/utils/images';
import clsx from 'clsx';
import { ImgHTMLAttributes } from 'react';
import { Link, useLocation } from 'react-router-dom';

export type ImageSizes =
    | 'original'
    | '2560xAUTO'
    | '1920xAUTO'
    | '1200xAUTO'
    | '1000xAUTO'
    | '960xAUTO'
    | '750xAUTO'
    | '500xAUTO'
    | '400xAUTO'
    | '368xAUTO'
    | '300xAUTO'
    | '225xAUTO'
    | '100xAUTO'
    | 'AUTOx1000'
    | 'AUTOx500'
    | '1920x1000'
    | '1200x800'
    | '960x500'
    | '750x500'
    | '600x400'
    | '480x250'
    | '450x340'
    | '229x300'
    | '225x170'
    | '200x150'
    | '110x82'
    | '100x65'
    | '7x16'
    | '7x12'
    | '7x10'
    | '7x9'
    | '7x8'
    | '7x7'
    | '7x6'
    | '7x5'
    | '7x4'
    | '7x3';

export type ImageProps = {
    src?: string;
    width?: number;
    height?: number;
    size?: ImageSizes;
    altText?: string | null;
    noShadow?: boolean;
    noMargin?: boolean;
    text?: null | string;
    link?: null | {
        title: string;
        url: string;
    };
    focus?: null | {
        x: number;
        y: number;
    };
    useLargeCorner?: boolean;
    largeCornerPositions?: Array<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'>;
};

export const Image = ({
    src,
    width,
    height,
    size = '1200xAUTO',
    noShadow = false,
    altText = null,
    text = null,
    link = null,
    focus = null,
    noMargin = false,
    useLargeCorner,
    largeCornerPositions,
}: ImageProps) => {
    const { pathname } = useLocation();
    if (!src) {
        return null;
    }

    const style: React.CSSProperties = {};
    if (focus?.x && focus?.y) {
        style.objectPosition = `${focus.x * 100}% ${focus.y * 100}%`;
    }

    const isDecorative = !altText && !text && !link?.url;

    const renderImage = () => {
        const props: ImgHTMLAttributes<HTMLImageElement> = {
            style,
            width,
            height,
            loading: 'lazy',
            src: getImageUrl(src, size),
            className: clsx({
                'r-rounded-2xl': !useLargeCorner,
                'r-rounded-tl-[90px]': useLargeCorner && largeCornerPositions?.includes('top-left'),
                'r-rounded-tr-[90px]': useLargeCorner && largeCornerPositions?.includes('top-right'),
                'r-rounded-bl-[90px]': useLargeCorner && largeCornerPositions?.includes('bottom-left'),
                'r-rounded-br-[90px]': useLargeCorner && largeCornerPositions?.includes('bottom-right'),
                'r-shadow-lg': !noShadow,
                'r-mb-6': !noMargin,
            }),
        };

        props.alt = altText ?? '';

        return <img {...props} />;
    };

    return (
        <figure data-component="image" {...(isDecorative && { 'aria-hidden': true })}>
            {link?.url ? <Link to={link?.url ?? pathname}>{renderImage()}</Link> : renderImage()}
            {text ? <p className="r-pt-1 r-text-center r-text-sm">{text}</p> : null}
        </figure>
    );
};
